import { format } from 'date-fns';
import { useSelector } from 'react-redux';

import ProjectGroupsModule from '../../../redux/project_grouping';
import type { ProjectGroupRelease } from '../../../types';

type SbomPublishState = {
  /** Whether the release's SBOM has finished publishing to the portal. */
  isPublished: boolean;
  /** Whether a publish is queued but not yet complete. */
  isPending: boolean;
  /** Human-readable time the SBOM was last published, or null if never. */
  lastPublishedAt: string | null;
};

/**
 * Reads the SBOM portal publish state of the currently-loaded release group
 * release from the (legacy) project grouping redux store.
 *
 * Reads defensively: `release.data` is `null` until a release is loaded (and is
 * never loaded in project scope), whereas the published-status redux selectors
 * assume a non-null release and would throw.
 */
export function useSbomPublishState(): SbomPublishState {
  const release = useSelector(ProjectGroupsModule.selectors.getRelease) as ProjectGroupRelease | null;

  /*
   * Mirrors the format of the `getReportLastPublishedAt` selector, but read off
   * the release defensively so it is safe to call before a release is loaded
   * (the selector assumes a non-null release and would throw). The validity
   * check guards against a malformed date, which date-fns would otherwise throw on.
   */
  const publishedDate = release?.publishedAt ? new Date(release.publishedAt) : null;
  const lastPublishedAt = publishedDate && !Number.isNaN(publishedDate.getTime())
    ? format(publishedDate, 'MMMM do yyyy, h:mm:ss aaa')
    : null;

  return {
    isPublished: !!release?.publishedAt,
    isPending: release?.publishedOnPortal === 'pending',
    lastPublishedAt,
  };
}
